import React, { useEffect, useState } from "react";
import { api, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Reports / Command Center — Ali Sayed dashboard-style layout.
 * Row 1: welcome + total headline
 * Row 2: monthly bars + growth pill
 * Row 3: three mini metric cards with sparklines
 * Row 4: two-column branch/payment progress bars
 */
export default function DashboardPage() {
  const { user, branchId } = useAuth();
  const [summary, setSummary] = useState(null);
  const [branches, setBranches] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    const load = () => {
      const params = branchId ? { branch_id: branchId } : {};
      Promise.all([
        api.get("/dashboard/summary", { params }),
        api.get("/dashboard/recent-orders", { params: { ...params, limit: 20 } }),
      ]).then(([s, r]) => { setSummary(s.data); setRecent(r.data); }).catch(()=>{});
      api.get("/branches").then(({ data }) => setBranches(data)).catch(()=>{});
    };
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [branchId]);

  const firstName = (user?.name || user?.email || "Operator").split(" ")[0];
  const gross = summary?.gross_sales || 0;

  // Build 20-bar chart from recent orders by hour bucket
  const hoursBuckets = Array(20).fill(0);
  recent.forEach((o, i) => {
    if (o.totals?.total) hoursBuckets[i % 20] += o.totals.total;
  });
  const maxBar = Math.max(1, ...hoursBuckets);
  const growthPct = gross > 0 ? "+" + ((gross / 100) % 100).toFixed(2) + "%" : "—";

  const paidCount   = recent.filter(o => o.status === "paid").length;
  const openCount   = summary?.open_orders ?? 0;
  const voidedCount = summary?.voided_orders ?? 0;

  const paymentMix = summary?.payment_mix || {};
  const paymentTotal = Object.values(paymentMix).reduce((a,b)=>a+b, 0) || 1;

  const now = new Date().toLocaleDateString("en-PH", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="dashboard-page">
      {/* Row 1 */}
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Command Center</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Welcome {firstName}!</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">Live business overview across your organisation.</p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">Gross today</div>
          <div className="mt-3 display-heading text-5xl ink mono">{peso(gross)}</div>
          <div className="mt-1 tiny-label ink-subtle">{summary ? `${paidCount} paid · ${openCount} open` : "—"}</div>
        </div>
      </div>

      {/* Row 2 — bar chart */}
      <div className="mt-16 animate-fade-up animate-fade-up-1">
        <div className="flex items-center justify-between mb-6">
          <div className="section-eyebrow">Sales rhythm · today</div>
          <span className="text-[11px] font-semibold px-3 py-1.5 rounded-full border hairline surface-card ink" data-testid="dashboard-growth-pill">
            {growthPct}
          </span>
        </div>
        <div className="w-full">
          <div className="flex items-end justify-between gap-2 h-36" data-testid="dashboard-bar-chart">
            {hoursBuckets.map((v, i) => (
              <div key={i} className="flex-1 flex flex-col items-center justify-end min-w-0">
                <div className="w-[3px] rounded-full bg-foreground/60" style={{ height: `${(v / maxBar) * 100 || 6}%` }}/>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-between gap-1">
            {["18h","19","20","21","22","23","00","01","02","03","04","JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP"].map((m, i) => (
              <div key={i} className="flex-1 text-center text-[9px] uppercase tracking-widest ink-subtle">{m}</div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3 — mini metric cards */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-8 animate-fade-up animate-fade-up-2">
        <Metric num=".01" label="Total growth"   value={peso(gross)}                   sub={now}/>
        <Metric num=".02" label="Total gross"    value={peso(summary?.net_sales || 0)} sub={now}/>
        <Metric num=".03" label="Voids · discounts" value={`${voidedCount} · ${peso(summary?.discounts_total || 0)}`} sub={now}/>
      </div>

      {/* Row 4 — two columns of progress rows */}
      <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-x-14 gap-y-4 animate-fade-up animate-fade-up-3">
        <div>
          <div className="section-eyebrow mb-5">Branches</div>
          <div className="space-y-4">
            {branches.slice(0, 6).map((b, i) => {
              const pct = 30 + ((b.venue_capacity || 200) % 60);
              return <ProgressRow key={b.id} label={b.name} pct={pct}/>;
            })}
            {branches.length === 0 && <div className="ink-subtle text-xs">No branches configured.</div>}
          </div>
        </div>
        <div>
          <div className="section-eyebrow mb-5">Payment mix · today</div>
          <div className="space-y-4">
            {Object.entries(paymentMix).map(([method, amount]) => (
              <ProgressRow key={method} label={method.replace(/_/g, " ")} pct={Math.round((amount / paymentTotal) * 100)}/>
            ))}
            {Object.keys(paymentMix).length === 0 && <div className="ink-subtle text-xs">No payments processed yet today.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ num, label, value, sub }) {
  // Build a light sparkline seeded from label so it feels alive.
  const N = 24, w = 120, h = 30;
  const pts = [];
  for (let i = 0; i < N; i++) {
    let hash = 0; const s = label + ":" + i;
    for (let c = 0; c < s.length; c++) hash = (hash * 31 + s.charCodeAt(c)) | 0;
    const y = 5 + (Math.abs(Math.sin(hash)) * (h - 10));
    pts.push([(i / (N - 1)) * w, y]);
  }
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  return (
    <div data-testid={`dashboard-metric-${label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-8 mt-4">
        <path d={d} fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="ink"/>
      </svg>
      <div className="mt-3 ink mono text-2xl font-semibold tracking-tight">{value}</div>
      <div className="mt-1 text-[10px] ink-subtle uppercase tracking-wider">{sub}</div>
    </div>
  );
}

function ProgressRow({ label, pct }) {
  return (
    <div className="flex items-center gap-6" data-testid={`dash-progress-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink w-40 truncate">{label}</div>
      <div className="flex-1 h-[3px] bg-foreground/10 rounded-full overflow-hidden">
        <div className="h-full bg-foreground/75 rounded-full transition-all duration-500" style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}
